import { useState, useCallback } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useUpdateUser, type UserListItem, type UpdateUserInput } from '@/hooks/useUsers';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  role?: string;
}

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: UserListItem | null;
}

/**
 * EditUserModal Component
 *
 * Modal dialog for editing an existing user (admin only).
 * Matches the approved UI design in docs/views/settings.html lines 1069-1160.
 *
 * Tasks: M4.5.4.1, M4.5.4.2, M4.5.4.3, M4.5.4.4, M4.5.4.5
 */
export function EditUserModal({ open, onClose, user }: EditUserModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  // Initialize form with user data directly (M4.5.4.2)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserListItem['role']>('user');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const updateUser = useUpdateUser();

  // Sync form with user data when opening (controlled initialization)
  if (open && user && !isInitialized) {
    setName(user.name);
    setEmail(user.email);
    setPassword('');
    setRole(user.role);
    setErrors({});
    setShowPassword(false);
    setIsInitialized(true);
  }

  // Reset initialization flag when closed
  if (!open && isInitialized) {
    setIsInitialized(false);
  }

  // Handle close and reset
  const handleClose = useCallback(() => {
    setName('');
    setEmail('');
    setPassword('');
    setRole('user');
    setErrors({});
    setShowPassword(false);
    onClose();
  }, [onClose]);

  // Handle input changes and clear errors
  const handleNameChange = (value: string) => {
    setName(value);
    if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
  };

  const handleRoleChange = (value: UserListItem['role']) => {
    setRole(value);
    if (errors.role) setErrors(prev => ({ ...prev, role: undefined }));
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation (M4.5.4.2)
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    // Email validation (M4.5.4.2)
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation - optional for edit (M4.5.4.3)
    // Only validate if password is provided (blank = no change)
    if (password && password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission (M4.5.4.4)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user) {
      return;
    }

    try {
      // Build update data - only include changed fields
      const updateData: UpdateUserInput = {};

      // Include name if changed
      if (name.trim() !== user.name) {
        updateData.name = name.trim();
      }

      // Include email if changed
      if (email.trim().toLowerCase() !== user.email.toLowerCase()) {
        updateData.email = email.trim().toLowerCase();
      }

      // Include password only if provided (blank = no change)
      if (password) {
        updateData.password = password;
      }

      // Include role if changed
      if (role !== user.role) {
        updateData.role = role;
      }

      // Only call API if there are changes
      if (Object.keys(updateData).length > 0) {
        await updateUser.mutateAsync({
          id: user.id,
          data: updateData,
        });
      }

      handleClose();
    } catch {
      // Error is handled by the mutation's onError callback
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent className="w-full max-w-md p-6" onClose={handleClose}>
        <DialogHeader className="p-0 mb-4">
          <DialogTitle className="text-lg font-semibold text-foreground">Edit User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name Input (M4.5.4.2) */}
          <Input
            value={name}
            onChange={e => handleNameChange(e.target.value)}
            label="Full Name"
            placeholder="John Smith"
            required
            error={errors.name}
            disabled={updateUser.isPending}
          />

          {/* Email Input (M4.5.4.2) */}
          <Input
            type="email"
            value={email}
            onChange={e => handleEmailChange(e.target.value)}
            label="Email Address"
            placeholder="john@company.com"
            required
            error={errors.email}
            disabled={updateUser.isPending}
          />

          {/* Password Input - Optional (M4.5.4.3) */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              Password{' '}
              <span className="text-muted-foreground font-normal">
                (leave blank to keep current)
              </span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => handlePasswordChange(e.target.value)}
                className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                  errors.password ? 'border-destructive' : 'border-input'
                }`}
                placeholder="••••••••"
                disabled={updateUser.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password ? (
              <p className="text-sm text-destructive">{errors.password}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Only enter a new password if you want to change it
              </p>
            )}
          </div>

          {/* Role Selector (M4.5.4.2) */}
          <Select
            value={role}
            onChange={e => handleRoleChange(e.target.value as 'admin' | 'user')}
            label="Role"
            error={errors.role}
            disabled={updateUser.isPending}
          >
            <option value="user">Standard User</option>
            <option value="admin">Administrator</option>
          </Select>
          <p className="text-xs text-muted-foreground -mt-2">
            Administrators can manage users and delete records
          </p>

          {/* Footer Actions (M4.5.4.5) */}
          <DialogFooter className="p-0 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={updateUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateUser.isPending}>
              {updateUser.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update User
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
